<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * LaisChecklistPanel — data-driven checklist for transação / contrato / KYC /
 * vistoria / due diligence flows.
 *
 * Every row carries a normalized status (done/pending/blocked/overdue/na) that
 * maps to a leading indicator icon and to the canonical LaisStatusBadge tone —
 * an unrecognized status falls back to neutral, never to success. Evidence is
 * redacted by default when either the row or the whole panel is redacted, so a
 * raw evidence label is never shown once protected. Overdue emphasis is only
 * derived when a `referenceNow` is passed in; the component never reads the
 * clock itself, keeping renders deterministic.
 */
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
    validator: value => value.every(item => item && typeof item === 'object'),
  },
  title: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  loadingRows: { type: Number, default: 3 },
  emptyLabel: { type: String, default: '' },
  redacted: { type: Boolean, default: false },
  showStatus: { type: Boolean, default: true },
  toggleable: { type: Boolean, default: false },
  // Reference instant for overdue emphasis. Left undefined the panel shows the
  // deadline with the given status and never computes "now" on its own.
  referenceNow: { type: [String, Number, Date], default: undefined },
});

const emit = defineEmits(['action', 'toggle']);

const { t } = useI18n();

const STATUS_META = Object.freeze({
  done: { icon: 'i-lucide-check-circle-2', tone: 'positive' },
  pending: { icon: 'i-lucide-circle', tone: 'neutral' },
  blocked: { icon: 'i-lucide-ban', tone: 'danger' },
  overdue: { icon: 'i-lucide-clock', tone: 'warning' },
  na: { icon: 'i-lucide-minus-circle', tone: 'neutral' },
});

const UNKNOWN_META = Object.freeze({
  icon: 'i-lucide-circle-help',
  tone: 'neutral',
});

const ICON_TONE_CLASS = Object.freeze({
  neutral: 'text-lais-text-muted',
  positive: 'text-lais-mint-dark',
  warning: 'text-lais-warning',
  danger: 'text-lais-danger',
  info: 'text-lais-primary',
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const instance = getCurrentInstance();
const baseId = `lais-checklist-panel-${instance?.uid || 'default'}`;
const titleId = `${baseId}-title`;

const toDate = value => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const iconToneClass = tone => ICON_TONE_CLASS[tone] || ICON_TONE_CLASS.neutral;

// Static keys keep i18n extraction happy (no dynamic-key lookups).
const statusLabelFor = key => {
  switch (key) {
    case 'done':
      return t('LAIS_CHECKLIST_PANEL.STATUS.DONE');
    case 'pending':
      return t('LAIS_CHECKLIST_PANEL.STATUS.PENDING');
    case 'blocked':
      return t('LAIS_CHECKLIST_PANEL.STATUS.BLOCKED');
    case 'overdue':
      return t('LAIS_CHECKLIST_PANEL.STATUS.OVERDUE');
    case 'na':
      return t('LAIS_CHECKLIST_PANEL.STATUS.NA');
    default:
      return t('LAIS_CHECKLIST_PANEL.STATUS.UNKNOWN');
  }
};

const formatDeadline = value => {
  const date = toDate(value);
  if (!date) return value ? String(value) : '';
  return dateFormatter.format(date);
};

const referenceTime = computed(() => {
  const date = toDate(props.referenceNow);
  return date ? date.getTime() : null;
});

const isPastDeadline = item => {
  if (referenceTime.value === null) return false;
  const date = toDate(item.deadline);
  if (!date) return false;
  const normalizedStatus = String(item.status || '').toLowerCase();
  return date.getTime() < referenceTime.value && normalizedStatus !== 'done';
};

const rows = computed(() =>
  (props.items || []).map((item, index) => {
    const normalizedStatus = String(item.status || '').toLowerCase();
    const meta = STATUS_META[normalizedStatus] || UNKNOWN_META;
    const metaKey = STATUS_META[normalizedStatus]
      ? normalizedStatus
      : 'unknown';
    const evidenceHidden = props.redacted || item.evidenceRedacted === true;
    return {
      raw: item,
      key: item.id ?? `row-${index}`,
      label: item.label ?? '',
      meta,
      statusLabel: statusLabelFor(metaKey),
      owner: item.owner || '',
      deadlineText: formatDeadline(item.deadline),
      isOverdueDeadline: isPastDeadline(item),
      blocker: item.blocker || '',
      evidenceHidden,
      evidenceLabel: evidenceHidden ? '' : item.evidenceLabel || '',
      actionLabel: item.actionLabel || '',
    };
  })
);

const isEmpty = computed(() => !props.loading && rows.value.length === 0);
const resolvedEmptyLabel = computed(
  () => props.emptyLabel || t('LAIS_CHECKLIST_PANEL.EMPTY')
);
const skeletonRows = computed(() => Math.max(1, props.loadingRows));
const accessibleLabel = computed(() =>
  props.title ? undefined : t('LAIS_CHECKLIST_PANEL.LANDMARK')
);

const toggleLabel = row => `${t('LAIS_CHECKLIST_PANEL.TOGGLE')}: ${row.label}`;

const handleAction = row => emit('action', row.raw);
const handleToggle = row => emit('toggle', row.raw);
</script>

<template>
  <section
    class="flex w-full flex-col overflow-hidden rounded-lais border border-lais-border-soft bg-lais-bg-card shadow-lais-card"
    :aria-labelledby="title ? titleId : undefined"
    :aria-label="accessibleLabel"
    :aria-busy="loading ? 'true' : undefined"
  >
    <header
      v-if="title"
      class="flex items-center gap-2 border-b border-lais-border-soft px-4 py-3"
    >
      <span
        class="i-lucide-list-checks size-4 shrink-0 text-lais-primary"
        aria-hidden="true"
      />
      <h2
        :id="titleId"
        class="truncate font-redHatDisplay text-lais-heading-sm font-semibold leading-tight text-lais-text"
      >
        {{ title }}
      </h2>
    </header>

    <ul
      v-if="loading"
      class="animate-pulse divide-y divide-lais-border-soft"
      aria-hidden="true"
    >
      <li
        v-for="n in skeletonRows"
        :key="`skeleton-${n}`"
        class="flex items-center gap-3 px-4 py-3.5"
      >
        <span class="size-5 shrink-0 rounded-full bg-lais-bg-soft" />
        <div class="min-w-0 flex-1 space-y-2">
          <span class="block h-3 w-2/5 rounded-lais-sm bg-lais-bg-soft" />
          <span class="block h-2.5 w-3/5 rounded-lais-sm bg-lais-bg-soft" />
        </div>
      </li>
    </ul>

    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center gap-2 px-4 py-8 text-center"
    >
      <span
        class="i-lucide-clipboard-list size-8 text-lais-text-muted"
        aria-hidden="true"
      />
      <p class="text-lais-body-sm text-lais-text-helper">
        {{ resolvedEmptyLabel }}
      </p>
    </div>

    <ul v-else role="list" class="divide-y divide-lais-border-soft">
      <li
        v-for="row in rows"
        :key="row.key"
        class="flex items-start gap-3 px-4 py-3.5"
        :data-status-tone="row.meta.tone"
      >
        <button
          v-if="toggleable"
          type="button"
          class="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full transition-colors hover:bg-lais-bg-soft focus-visible:outline-none focus-visible:shadow-lais-focus"
          :aria-label="toggleLabel(row)"
          @click="handleToggle(row)"
        >
          <span
            class="size-5"
            :class="[row.meta.icon, iconToneClass(row.meta.tone)]"
            aria-hidden="true"
          />
        </button>
        <span v-else class="mt-0.5 grid size-6 shrink-0 place-items-center">
          <span
            class="size-5"
            :class="[row.meta.icon, iconToneClass(row.meta.tone)]"
            aria-hidden="true"
          />
        </span>

        <div class="min-w-0 flex-1">
          <div
            class="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5"
          >
            <p
              class="min-w-0 flex-1 text-lais-body-md font-medium text-lais-text"
            >
              {{ row.label }}
            </p>
            <LaisStatusBadge
              v-if="showStatus"
              size="sm"
              :tone="row.meta.tone"
              :label="row.statusLabel"
            />
          </div>

          <div
            v-if="
              row.owner ||
              row.deadlineText ||
              row.evidenceHidden ||
              row.evidenceLabel
            "
            class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-lais-body-xs"
          >
            <span
              v-if="row.owner"
              class="inline-flex items-center gap-1 text-lais-text-muted"
            >
              <span
                class="i-lucide-user size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span class="truncate">{{ row.owner }}</span>
            </span>

            <span
              v-if="row.deadlineText"
              class="inline-flex items-center gap-1"
              :class="
                row.isOverdueDeadline
                  ? 'font-semibold text-lais-danger'
                  : 'text-lais-text-muted'
              "
            >
              <span
                class="i-lucide-calendar-clock size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span>{{ row.deadlineText }}</span>
              <span
                v-if="row.isOverdueDeadline"
                class="ml-0.5 rounded-lais-sm bg-lais-danger-50 px-1.5 py-0.5 text-lais-label-xs font-semibold uppercase text-lais-danger"
              >
                {{ t('LAIS_CHECKLIST_PANEL.OVERDUE_FLAG') }}
              </span>
            </span>

            <span
              v-if="row.evidenceHidden"
              class="inline-flex items-center gap-1 text-lais-text-muted"
            >
              <span
                class="i-lucide-lock size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('LAIS_CHECKLIST_PANEL.EVIDENCE_PROTECTED') }}</span>
            </span>
            <span
              v-else-if="row.evidenceLabel"
              class="inline-flex min-w-0 items-center gap-1 text-lais-text-body"
            >
              <span
                class="i-lucide-paperclip size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span class="truncate">{{ row.evidenceLabel }}</span>
            </span>
          </div>

          <p
            v-if="row.blocker"
            class="mt-1.5 inline-flex items-start gap-1 text-lais-body-xs text-lais-danger"
          >
            <span
              class="i-lucide-triangle-alert mt-px size-3.5 shrink-0"
              aria-hidden="true"
            />
            <span class="min-w-0">{{ row.blocker }}</span>
          </p>
        </div>

        <button
          v-if="row.actionLabel"
          type="button"
          class="mt-0.5 inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-lais-sm border border-lais-border-soft bg-lais-bg-card px-3 py-1.5 font-redHatText text-lais-label-sm font-semibold text-lais-text-body transition-colors hover:border-lais-primary/50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          :aria-label="row.actionLabel"
          @click="handleAction(row)"
        >
          {{ row.actionLabel }}
        </button>
      </li>
    </ul>
  </section>
</template>
